import { useRef, type PointerEvent as ReactPointerEvent } from 'react';
import { rotatedBounds, unionRects, type Point, type Rect } from '../../doc/geometry';
import { MIN_ITEM_SIZE, type Geometry, type Item } from '../../doc/schema';
import { commitTransient, withTransient } from '../actions';
import { useBoard, useItemsVersion, usePane, usePaneStore, useReadOnly } from '../context';
import { screenToWorld, worldToScreen } from '../viewport/camera';
import {
  CORNER_HANDLES,
  RESIZE_HANDLES,
  resizeGeometry,
  rotateGeometry,
  scaleGroup,
  type Handle,
} from './transforms';
import { collectCandidates, snapEdge, snapThreshold, type SnapCandidates } from './snapping';
import { toolSettings } from '../toolSettings';
import { expandRect } from '../../doc/geometry';
import { visibleWorldRect } from '../viewport/camera';

type Drag =
  | { kind: 'resize'; pointerId: number; item: Geometry; id: string; handle: Handle; snap: SnapCandidates | null }
  | { kind: 'rotate'; pointerId: number; item: Geometry; id: string; startWorld: Point }
  | { kind: 'scale'; pointerId: number; starts: Map<string, Geometry>; bbox: Rect; handle: Handle };

/**
 * Selection box and handles, drawn in screen space (a sibling of the world layer) so handle
 * sizes stay constant at any zoom. Single selection gets resize + rotate; multi gets corner scale.
 */
export function SelectionOverlay() {
  const board = useBoard();
  const pane = usePaneStore();
  useItemsVersion();
  const selection = usePane((s) => s.selection);
  const camera = usePane((s) => s.camera);
  const transient = usePane((s) => s.transient);
  const editingId = usePane((s) => s.editingId);
  const readOnly = useReadOnly();
  const drag = useRef<Drag | null>(null);

  const items: Item[] = [];
  for (const id of selection) {
    const item = board.index.get(id);
    if (item) items.push(withTransient(item, transient[id]));
  }
  if (!items.length) return null;

  const toWorld = (e: ReactPointerEvent): Point => {
    const host = (e.currentTarget as HTMLElement).closest('.viewport');
    const r = host?.getBoundingClientRect();
    const local = r ? { x: e.clientX - r.left, y: e.clientY - r.top } : { x: e.clientX, y: e.clientY };
    return screenToWorld(pane.getState().camera, local);
  };

  const begin = (e: ReactPointerEvent, d: Drag) => {
    e.stopPropagation();
    e.preventDefault();
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    drag.current = d;
  };

  const onMove = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    const world = toWorld(e);
    const s = pane.getState();
    if (d.kind === 'resize') {
      const isImage = board.index.get(d.id)?.type === 'image';
      const keepAspect = isImage ? !e.altKey : e.shiftKey;
      const g = resizeGeometry(d.item, d.handle, world, { keepAspect, minSize: MIN_ITEM_SIZE });
      // Snap the dragged edges of an unrotated, free-aspect resize.
      let gx: number | null = null;
      let gy: number | null = null;
      if (d.snap && !keepAspect && !d.item.rotation && !e.altKey) {
        const th = snapThreshold(s.camera.zoom);
        const grid = toolSettings.getState().snapGrid ? ((board.meta.get('gridSize') as number | undefined) ?? 32) : null;
        if (d.handle.sx === 1) {
          const r = snapEdge(g.x + g.w, d.snap.xs, th, grid);
          g.w = Math.max(MIN_ITEM_SIZE, r.pos - g.x);
          gx = r.guide;
        } else if (d.handle.sx === -1) {
          const r = snapEdge(g.x, d.snap.xs, th, grid);
          const right = g.x + g.w;
          g.x = Math.min(r.pos, right - MIN_ITEM_SIZE);
          g.w = right - g.x;
          gx = r.guide;
        }
        if (d.handle.sy === 1) {
          const r = snapEdge(g.y + g.h, d.snap.ys, th, grid);
          g.h = Math.max(MIN_ITEM_SIZE, r.pos - g.y);
          gy = r.guide;
        } else if (d.handle.sy === -1) {
          const r = snapEdge(g.y, d.snap.ys, th, grid);
          const bottom = g.y + g.h;
          g.y = Math.min(r.pos, bottom - MIN_ITEM_SIZE);
          g.h = bottom - g.y;
          gy = r.guide;
        }
      }
      s.setSnapGuides({ x: gx, y: gy });
      s.setTransient({ [d.id]: g });
    } else if (d.kind === 'rotate') {
      const rotation = rotateGeometry(d.item, d.startWorld, world, e.shiftKey ? 15 : null);
      s.setTransient({ [d.id]: { rotation } });
    } else {
      s.setTransient(scaleGroup(d.starts, d.bbox, d.handle, world, MIN_ITEM_SIZE));
    }
  };

  const onUp = (e: ReactPointerEvent) => {
    const d = drag.current;
    if (!d || d.pointerId !== e.pointerId) return;
    drag.current = null;
    (e.currentTarget as HTMLElement).releasePointerCapture(e.pointerId);
    pane.getState().setSnapGuides({ x: null, y: null });
    commitTransient({ board, pane });
  };

  const single = items.length === 1 ? items[0] : undefined;
  const anyLocked = readOnly || items.some((i) => board.isLocked(i));

  if (single) {
    const cx = single.x + single.w / 2;
    const cy = single.y + single.h / 2;
    const sc = worldToScreen(camera, { x: cx, y: cy });
    const w = single.w * camera.zoom;
    const h = single.h * camera.zoom;
    const showHandles = !readOnly && editingId !== single.id && !board.isLocked(single);
    return (
      <div
        className={`sel-box${anyLocked ? ' sel-box-locked' : ''}`}
        style={{
          left: sc.x - w / 2,
          top: sc.y - h / 2,
          width: w,
          height: h,
          transform: `rotate(${single.rotation}deg)`,
        }}
      >
        {showHandles &&
          RESIZE_HANDLES.map((hd) => (
            <div
              key={`${hd.sx},${hd.sy}`}
              className="handle"
              data-cursor={cursorFor(hd)}
              style={{ left: `${(hd.sx + 1) * 50}%`, top: `${(hd.sy + 1) * 50}%` }}
              onPointerDown={(e) => {
                const s = pane.getState();
                const snap = toolSettings.getState().snap
                  ? collectCandidates(board.index, expandRect(visibleWorldRect(s.camera, s.size), 0.5), new Set([single.id]))
                  : null;
                begin(e, { kind: 'resize', pointerId: e.pointerId, item: single, id: single.id, handle: hd, snap });
              }}
              onPointerMove={onMove}
              onPointerUp={onUp}
              onPointerCancel={onUp}
            />
          ))}
        {showHandles && (
          <div
            className="handle handle-rotate"
            onPointerDown={(e) =>
              begin(e, {
                kind: 'rotate',
                pointerId: e.pointerId,
                item: single,
                id: single.id,
                startWorld: toWorld(e),
              })
            }
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
          />
        )}
      </div>
    );
  }

  const bbox = unionRects(items.map(rotatedBounds));
  if (!bbox) return null;
  const tl = worldToScreen(camera, bbox);
  return (
    <div
      className={`sel-box sel-box-multi${anyLocked ? ' sel-box-locked' : ''}`}
      style={{ left: tl.x, top: tl.y, width: bbox.w * camera.zoom, height: bbox.h * camera.zoom }}
    >
      {!anyLocked &&
        CORNER_HANDLES.map((hd) => (
          <div
            key={`${hd.sx},${hd.sy}`}
            className="handle"
            data-cursor={cursorFor(hd)}
            style={{ left: `${(hd.sx + 1) * 50}%`, top: `${(hd.sy + 1) * 50}%` }}
            onPointerDown={(e) => {
              const starts = new Map<string, Geometry>();
              for (const i of items) starts.set(i.id, i);
              begin(e, { kind: 'scale', pointerId: e.pointerId, starts, bbox, handle: hd });
            }}
            onPointerMove={onMove}
            onPointerUp={onUp}
            onPointerCancel={onUp}
          />
        ))}
    </div>
  );
}

function cursorFor(h: Handle): string {
  if (h.sx && h.sy) return h.sx === h.sy ? 'nwse' : 'nesw';
  return h.sx ? 'ew' : 'ns';
}
